// client/src/pages/ChoosePlan.jsx
import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { useNavigate, useSearchParams } from 'react-router-dom';
import OnboardGuard from '../components/OnboardGuard';
import OnboardingProgressBar from '../components/common/OnboardingProgressBar';
import { CreditCard, Check } from 'lucide-react';

const Logo = ({ size = 'md', className = '' }) => {
  const sizes = {
    sm: 'h-12',
    md: 'h-20',
    lg: 'h-64',
    xl: 'max-h-[420px]',
  };
  return (
    <img
      src="/STB_LOGO_V1.png"
      alt="SecureTheBag Logo"
      className={`${sizes[size] || sizes.md} w-auto object-contain ${className}`}
    />
  );
};

const Footer = () => (
  <footer className="mt-auto py-4 text-center text-sm text-gray-500">
    © {new Date().getFullYear()} SecureTheBag
  </footer>
);

// Fallback feature list if Stripe product metadata is empty.
const DEFAULT_FEATURES = [
  'Unlimited bank accounts',
  '8-week cash flow forecast with overdraft warnings',
  'AI recurring-bill detection and transaction matching',
  'SMS alerts and daily morning digest',
  'Business entity separation (S-Corp / LLC)',
  'Cancel anytime — no long-term contract',
];

function formatPrice(cents, currency = 'usd') {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);
}

function ChoosePlanForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const wasCancelled = searchParams.get('cancelled') === 'true';
  const [plans, setPlans] = useState([]);
  const [selected, setSelected] = useState('annual'); // default to best value
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/api/subscriptions/plans');
        setPlans(res.data.plans || []);
      } catch (err) {
        console.error('Failed to fetch plans:', err);
        setError('Could not load subscription plans. Please try again later.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Clear stale sessionStorage state from prior cancelled attempt so we don't
  // auto-re-create a checkout session with a stale priceId.
  useEffect(() => {
    if (wasCancelled) {
      try { sessionStorage.removeItem('selectedPriceId'); } catch {}
    }
  }, [wasCancelled]);

  const monthly = plans.find((p) => p.key === 'monthly');
  const annual = plans.find((p) => p.key === 'annual');
  const plan = plans.find((p) => p.key === selected) || plans[0];

  let savingsPercent = null;
  if (monthly && annual && monthly.unit_amount > 0) {
    const yearlyAtMonthly = monthly.unit_amount * 12;
    if (annual.unit_amount < yearlyAtMonthly) {
      savingsPercent = Math.round(((yearlyAtMonthly - annual.unit_amount) / yearlyAtMonthly) * 100);
    }
  }

  const handleStartTrial = () => {
    if (!plan) return;
    try { sessionStorage.setItem('selectedPriceId', plan.id); } catch {
      // Safari private browsing throws — fall through, URL param carries priceId.
    }
    navigate(`/payment?priceId=${plan.id}`);
  };

  const features = plan?.features?.length ? plan.features : DEFAULT_FEATURES;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex flex-col">
      <div className="w-full py-6 px-4 border-b border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto flex justify-center">
          <Logo size="sm" />
        </div>
      </div>

      <OnboardingProgressBar currentStep="choose_plan" />

      <div className="flex-1 flex items-center justify-center p-6 sm:p-8">
        <div className="w-full max-w-md">
          <div className="mb-8 text-center">
            <div className="flex items-center justify-center mb-4">
              <div className="bg-indigo-100 p-3 rounded-full">
                <CreditCard className="w-6 h-6 text-indigo-600" />
              </div>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              Start your 7-day free trial
            </h1>
            <p className="text-gray-600 text-sm sm:text-base">
              No charge today. Cancel any time before day 7 and you won't be billed.
            </p>
          </div>

          {wasCancelled && (
            <div className="mb-6 rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
              Checkout was cancelled. Pick a plan below to try again — no charge yet.
            </div>
          )}

          {error && (
            <div className="mb-6 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
              <div className="font-semibold">Error</div>
              <p className="mt-1">{error}</p>
            </div>
          )}

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
              <p className="mt-4 text-gray-600">Loading plans...</p>
            </div>
          ) : plan ? (
            <>
              {/* Interval toggle */}
              <div className="inline-flex w-full rounded-lg bg-gray-100 p-1 mb-6">
                <button
                  type="button"
                  onClick={() => setSelected('monthly')}
                  className={`flex-1 min-h-[44px] px-4 py-2 text-sm font-medium rounded-md transition-all ${
                    selected === 'monthly'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Monthly
                </button>
                <button
                  type="button"
                  onClick={() => setSelected('annual')}
                  className={`flex-1 min-h-[44px] px-4 py-2 text-sm font-medium rounded-md transition-all ${
                    selected === 'annual'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Annual
                  {savingsPercent != null && savingsPercent > 0 && (
                    <span className="ml-2 inline-block text-xs font-semibold text-green-700">
                      Save {savingsPercent}%
                    </span>
                  )}
                </button>
              </div>

              {/* Plan card */}
              <div className="bg-white rounded-2xl shadow-md border border-gray-200 p-6 sm:p-8 mb-6">
                <h3 className="text-xl font-bold text-gray-900 text-center mb-1">
                  {plan.nickname || 'STB Pro'}
                </h3>

                <div className="text-center mb-6">
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-4xl sm:text-5xl font-bold text-gray-900">
                      {formatPrice(plan.unit_amount, plan.currency)}
                    </span>
                    <span className="text-gray-600 text-base">
                      /{plan.interval === 'year' ? 'year' : 'month'}
                    </span>
                  </div>
                  <p className="text-green-600 text-sm font-medium mt-1">
                    after 7-day free trial
                  </p>
                </div>

                <ul className="space-y-3">
                  {features.map((feat, idx) => (
                    <li key={idx} className="flex items-start gap-2.5">
                      <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-700 leading-relaxed">
                        {feat}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <button
                onClick={handleStartTrial}
                className="w-full min-h-[44px] py-3.5 px-6 rounded-lg font-semibold text-base bg-indigo-600 text-white hover:bg-indigo-700 shadow-md hover:shadow-lg transition-all"
              >
                Start 7-day free trial
              </button>

              <p className="text-xs text-gray-500 text-center mt-4">
                Card required to start trial. Have a promo code? Enter it on the next screen.
              </p>
            </>
          ) : (
            <div className="text-center text-gray-600">
              No plans available. Please contact support.
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default function ChoosePlan() {
  return (
    <OnboardGuard currentStep="choose_plan">
      <ChoosePlanForm />
    </OnboardGuard>
  );
}
